import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserDetailsFormContext } from 'selectors/userDetailsFormSelectors';
import './user-details-form.scss';
import { Form } from '@ampol/design-system';
import { getDetailedUser, adminGetDetailedUser, adminUpdateUser, updateUser } from '../../actions/userActions';
import Env from 'Env';
import { setUserDetailsButton } from 'actions/modalMetaActions';
import { fetchAssignableRoles } from 'actions/roleActions';
import { userDetailsFormSchema } from 'schemas/user-schemas';
import dayjs from 'dayjs';
import { trackCustomEvent } from 'utilities/app-insights';

type UserState = {
  userId: string;
  emailId?: string;
  assignedRoleName?: string;
};

type UserDetailsFormProps = {
  userState: UserState;
  canReadWrite: boolean;
};

type AssignableRole = {
  id: string;
  name: string;
};

type UserDetailsFormValues = {
  emailId: string;
  firstName: string;
  lastName: string;
  roleId: string | null;
  modifiedBy?: string;
  modifiedOn?: string;
  createdBy?: string;
  createdOn?: string;
  lastLoginOn?: string;
};

const UserDetailsForm: React.FC<UserDetailsFormProps> = ({ userState, canReadWrite }) => {
  const dispatch = useDispatch();
  const isAdminPortal = Env.isAdminPortal();

  const {
    assignedGrandfatherClientNumber,
    detailedUser,
    detailedUserLoaded,
    detailedUserError,
    adminDetailedUser,
    adminDetailedUserLoaded,
    adminDetailedUserError,
    loggedInUser,
    assignedAccountNumber,
    updateUserLoading,
    adminUpdateUserLoading,
    assignRolesList,
  } = useSelector(selectUserDetailsFormContext);

  const formId = 'edit-user-details';

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [formValues, setFormValues] = useState<UserDetailsFormValues>({
    emailId: '',
    firstName: '',
    lastName: '',
    roleId: '',
  });

  useEffect(() => {
    if (isAdminPortal && Object.keys(adminDetailedUser).length === 0) {
      dispatch(adminGetDetailedUser(userState.userId));
    } else if (assignedGrandfatherClientNumber && Object.keys(detailedUser).length === 0) {
      dispatch(getDetailedUser(userState.userId, assignedGrandfatherClientNumber));
    }

    if (assignRolesList.length === 0) {
      dispatch(fetchAssignableRoles(assignedGrandfatherClientNumber));
    }
  }, []);

  const assignableRolesData = useMemo(
    () =>
      (assignRolesList || [])
        .map((x: AssignableRole) => ({ label: x.name, value: x.id }))
        .concat(
          formValues?.roleId && !assignRolesList.some((x: AssignableRole) => x.id === formValues.roleId)
            ? [
              {
                label: userState.assignedRoleName,
                value: formValues.roleId,
              },
            ]
            : []
        ),
    [assignRolesList, formValues?.roleId, userState.assignedRoleName]
  );

  useEffect(() => {
    dispatch(
      setUserDetailsButton({
        formId: formId,
        inactive:
          submitButtonDisabled ||
          updateUserLoading ||
          adminUpdateUserLoading ||
          !(detailedUserLoaded || adminDetailedUserLoaded) ||
          !canReadWrite,
        type: updateUserLoading || adminUpdateUserLoading || !canReadWrite ? 'button' : 'submit',
      })
    );
  }, [submitButtonDisabled, updateUserLoading, adminUpdateUserLoading, canReadWrite]);

  const formatDateTime = (dateTime?: string | Date) => {
    return dateTime ? dayjs(dateTime).format('DD/MM/YYYY h:mmA') : undefined;
  };

  useEffect(() => {
    if (
      (detailedUser && Object.keys(detailedUser).length > 0) ||
      (adminDetailedUser && Object.keys(adminDetailedUser).length > 0)
    ) {
      const userDetails = (isAdminPortal ? adminDetailedUser : detailedUser) as any; // typed as {} in Redux store
      let roleId: string | null = null;

      if (!isAdminPortal && userDetails?.assignedClientRoles?.length > 0) {
        const assignedClientRole = userDetails.assignedClientRoles.find(
          (x: { grandfatherClientNumber: string; assignedClientRoleIds?: string[] }) =>
            x.grandfatherClientNumber === assignedGrandfatherClientNumber
        );

        roleId = canReadWrite ? assignedClientRole?.assignedClientRoleIds?.[0] : userState.assignedRoleName;
      }

      setFormValues({
        emailId: userDetails?.emailId,
        firstName: userDetails?.givenName,
        lastName: userDetails?.familyName,
        roleId,
        ...(isAdminPortal && {
          modifiedBy: userDetails?.modifiedByEmailAddress,
          modifiedOn: formatDateTime(userDetails?.modifiedOn),
          createdBy: userDetails?.createdByEmailAddress,
          createdOn: formatDateTime(userDetails?.createdOn),
          lastLoginOn: formatDateTime(userDetails?.lastLoginOn),
        }),
      });
    }
  }, [detailedUser, adminDetailedUser]);

  const handleFormStateChange = ({ isValid }: { isValid: boolean }) => {
    setSubmitButtonDisabled(!isValid);
  };

  const handleFormSubmit = (values: UserDetailsFormValues) => {
    const updateUserProfileRequest = {
      givenName: values.firstName,
      familyName: values.lastName,
      assignedRoleId: isAdminPortal ? null : values.roleId,
    };

    trackCustomEvent(
      'Submit Update User Details',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Update in User Details Modal to Update User Details. ',
      { selectedUserEmail: userState?.emailId, selectedUserId: userState?.userId }
    );

    if (isAdminPortal) {
      dispatch(adminUpdateUser(userState.userId, updateUserProfileRequest));
    } else {
      dispatch(updateUser(userState.userId, assignedGrandfatherClientNumber, updateUserProfileRequest));
    }
  };

  return (
    <>
      {detailedUserLoaded || adminDetailedUserLoaded || detailedUserError || adminDetailedUserError ? (
        <Form
          id={formId}
          schema={userDetailsFormSchema}
          defaultValues={formValues}
          onSubmit={handleFormSubmit}
          onStateChange={handleFormStateChange}
        >
          <div className='user-details-form__section'>
            <Form.Input label={'Email'} name={'emailId'} disabled={true} />
            <div className='user-details-form__split-col'>
              <Form.Input label={'First Name'} name={'firstName'} disabled={!canReadWrite} />
              <Form.Input label={'Last Name'} name={'lastName'} disabled={!canReadWrite} />
              {!isAdminPortal && (
                <Form.DropdownSearch
                  name='roleId'
                  label='Select Role'
                  placeholder={userState.assignedRoleName}
                  data={assignableRolesData}
                  disabled={!canReadWrite}
                  allowClear={false}
                />
              )}
            </div>
            {isAdminPortal && (
              <>
                <Form.Input label={'Modified By'} name={'modifiedBy'} disabled={true} />
                <Form.Input label={'Modified On'} name={'modifiedOn'} disabled={true} />
                <Form.Input label={'Created By'} name={'createdBy'} disabled={true} />
                <Form.Input label={'Created On'} name={'createdOn'} disabled={true} />
                <Form.Input label={'Last Login On'} name={'lastLoginOn'} disabled={true} />
              </>
            )}
          </div>
        </Form>
      ) : (
        <div className='forbidden-user'>Loading...</div>
      )}
    </>
  );
};

export default UserDetailsForm;
